import { useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import Seo from '../components/Seo';
import { TREATMENT_SLUGS, CATEGORIES } from '../data/categories';
import { PRICE_GUIDES } from '../data/prices';
import { ARTICLES } from '../data/articles';
import { trackSearch } from '../lib/tracking';

export default function Sok() {
  const params = new URLSearchParams(useLocation().search);
  const q = (params.get('q') || '').toLowerCase();

  const results = useMemo(() => {
    if (!q) return { treatments: [], clinics: [], articles: [], prices: [] };
    const treatments = Object.entries(TREATMENT_SLUGS).filter(([, name]) => name.toLowerCase().includes(q)).map(([, name]) => {
      const cat = CATEGORIES.find((c) => c.treatments.includes(name));
      return { name, href: `/behandlinger/${cat?.slug || 'estetikk-og-kosmetikk'}`, category: cat?.name };
    });
    const prices = Object.entries(PRICE_GUIDES).filter(([, g]) => g.treatment.toLowerCase().includes(q)).map(([slug, g]) => ({ slug, treatment: g.treatment, category: g.category }));
    const articles = ARTICLES.filter((a) => a.title.toLowerCase().includes(q) || a.excerpt.toLowerCase().includes(q));
    return { treatments, prices, articles };
  }, [q]);

  useEffect(() => {
    if (q) {
      const total = (results.treatments?.length || 0) + (results.prices?.length || 0) + (results.articles?.length || 0);
      trackSearch(q, total);
    }
  }, [q]); // eslint-disable-line

  const total = (results.treatments?.length || 0) + (results.prices?.length || 0) + (results.articles?.length || 0);

  return (
    <Layout>
      <Seo title="Søkeresultater | Specialists.no" noindex />
      <section className="bg-[#0A1F44] text-white py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-display text-3xl lg:text-4xl font-bold">Søkeresultater for "{q}"</h1>
          <p className="text-white/60 mt-2">{total} treff</p>
        </div>
      </section>
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          {total === 0 && (
            <div className="bg-white rounded-xl p-8 border border-[#0A1F44]/10">
              <p className="text-[#334155]">Ingen resultater for "<strong>{q}</strong>". Prøv f.eks.:</p>
              <div className="flex flex-wrap gap-2 mt-4">
                {['Botox', 'Fillers', 'Hårtransplantasjon', 'Tannbleking', 'Fysioterapi', 'Øyenlaser'].map((t) => (
                  <Link key={t} to={`/sok?q=${encodeURIComponent(t)}`} className="px-4 py-2 bg-[#FAF8F5] rounded-full text-sm text-[#0A1F44] hover:bg-[#0A1F44] hover:text-white">{t}</Link>
                ))}
              </div>
            </div>
          )}

          {results.treatments?.length > 0 && (
            <div>
              <h2 className="font-display text-xl font-bold text-[#0A1F44] mb-3">Behandlinger ({results.treatments.length})</h2>
              <div className="space-y-2">
                {results.treatments.map((r) => (
                  <Link key={r.name} to={r.href} className="block bg-white p-4 rounded-xl border border-[#0A1F44]/10 hover:border-[#0A1F44]/30">
                    <div className="font-semibold text-[#0A1F44]">{r.name}</div>
                    <div className="text-xs text-[#64748B]">{r.category}</div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {results.prices?.length > 0 && (
            <div>
              <h2 className="font-display text-xl font-bold text-[#0A1F44] mb-3">Prisguider ({results.prices.length})</h2>
              <div className="space-y-2">
                {results.prices.map((r) => (
                  <Link key={r.slug} to={`/priser/${r.slug}`} className="block bg-white p-4 rounded-xl border border-[#0A1F44]/10 hover:border-[#0A1F44]/30">
                    <div className="font-semibold text-[#0A1F44]">Priser: {r.treatment}</div>
                    <div className="text-xs text-[#64748B]">{r.category}</div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {results.articles?.length > 0 && (
            <div>
              <h2 className="font-display text-xl font-bold text-[#0A1F44] mb-3">Artikler ({results.articles.length})</h2>
              <div className="space-y-2">
                {results.articles.map((a) => (
                  <Link key={a.slug} to={`/artikler/${a.slug}`} className="block bg-white p-4 rounded-xl border border-[#0A1F44]/10 hover:border-[#0A1F44]/30">
                    <div className="font-semibold text-[#0A1F44]">{a.title}</div>
                    <div className="text-xs text-[#64748B]">{a.category} · {a.readingTime} min</div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
